import { derived, get, writable } from "svelte/store";
import api from "$utils/api";
import type {
  ForumSpacePermission,
  ForumThreadWithPermissions,
} from "@outverse-video/outverse-api-client-lib";
import type { ForumSpace, Thread } from "models/forums";
import type { IconType } from "$components/+blocks/OVIcon/OVIcon";
import { ThreadFilter } from "models/shared-types";
import { distinctByKey } from "$utils/collections";
import { favourites } from "./favourites";
import { Clock, DA, FilledUp, Reply, UA } from "$components/+blocks/OVIcon";

export class ThreadListSort {
  public static readonly Newest = new ThreadListSort("Newest", "-created", "Newest", UA);
  public static readonly Oldest = new ThreadListSort("Oldest", "created", "Oldest", DA);
  public static readonly LatestReply = new ThreadListSort(
    "LatestReply",
    "-last_replied_at",
    "Latest reply",
    Clock
  );
  public static readonly MostReplies = new ThreadListSort(
    "MostReplies",
    "-reply_count",
    "Most replies",
    Reply
  );
  public static readonly MostUpvotes = new ThreadListSort(
    "MostUpvotes",
    "-upvote_count",
    "Most votes",
    FilledUp
  );

  private constructor(
    public readonly key: string,
    public readonly backendFilter: string,
    public readonly displayName: string,
    public readonly icon: IconType
  ) {}
}

export function fetchThreads(
  communitySlug: string,
  forumSpaceUuid: string | undefined | Array<string>,
  _forceRefetchCounter: number,
  sort: ThreadListSort,
  pinned_first: boolean,
  unread: boolean,
  labels: string[],
  solved?: boolean,
  cursor?: string
) {
  return api.communitiesThreadsAnnotationsList({
    communitySlug,
    cursor,
    // @ts-ignore - arrays work fine (?X=abc&X=efg)
    forumSpaceUuid: forumSpaceUuid || undefined,
    ordering: (pinned_first ? "pinned_at," : "") + (sort || ThreadListSort.Newest).backendFilter,
    unread,
    solved,
    labelUuid: labels,
  });
}

export interface ForumSpaceState {
  forumSpace: ForumSpace;
  myPermissions: ForumSpacePermission[];
  threadIDs: string[];
  threads: { [key: string]: Thread };
  forumUuidFilter: Array<string> | string | undefined;
  currentOrdering: ThreadListSort | null;
  fetched: boolean;
  community: string;
}

const initialState: ForumSpaceState = {
  fetched: false,
  community: "",
  forumSpace: {
    uuid: "",
    title: "",
    emoji: "",
    color: "",
    description: "",
    spaceType: "forum",
    isSubscribedToActivity: false,
    newActivity: false,
    favourite: false,
    highValueNewThreadsCount: 0,
    highValueNewRepliesCount: 0,
    directRepliesCount: 0,
    newActivityCount: 0,
    permissions: [],
  },
  threadIDs: [],
  myPermissions: [],
  threads: {},
  forumUuidFilter: undefined,
  currentOrdering: null,
};

export type NextPrevious = {
  next?: string;
  previous?: string;
  nextCursor?: string;
  previousCursor?: string;
};

const forumStore = () => {
  const { subscribe, set, update } = writable<ForumSpaceState>(initialState);
  let fetchDebounce: NodeJS.Timeout | undefined;

  const setAllThreads = (
    newThreads: ForumThreadWithPermissions[],
    currentOrdering: ThreadListSort,
    forumSpaceUuid: Array<string>,
    append: boolean
  ) => {
    update((state) => {
      // check forum data matches current filter
      if (state.forumSpace.uuid && state.forumSpace.uuid !== forumSpaceUuid[0]) {
        return state;
      }
      const ids = [];
      for (const thread of newThreads || []) {
        const t = state.threads[thread.forumThread.uuid];
        // add/update existing thread if it exists

        const oldContent = state.threads[thread.forumThread.uuid]?.content;
        state.threads[thread.forumThread.uuid] = {
          ...t,
          ...thread.forumThread,
          // prevents content from flashing twice when loading new threads
          content: oldContent || thread.forumThread.content,
          repliers: thread.forumThread.repliers,
          myPermissions: { ...t?.myPermissions, ...thread.myPermissions },
        };
        ids.push(thread.forumThread.uuid);
      }

      // update list of threads that is used for display
      if (append) {
        state.threadIDs = [
          ...state.threadIDs,
          ...ids.filter((t1) => !state.threadIDs.find((t) => t == t1)),
        ];
      } else {
        state.threadIDs = ids;
      }
      return {
        ...state,
        // @ts-ignore
        forumUuidFilter: forumSpaceUuid,
        currentOrdering,
      };
    });
  };

  return {
    subscribe,
    update,
    set,
    init: (communitySlug: string) => {
      set({
        community: communitySlug,
        fetched: false,
        forumSpace: {
          uuid: "",
          title: "",
          emoji: "",
          color: "",
          description: "",
          spaceType: "forum",
          isSubscribedToActivity: false,
          newActivity: false,
          favourite: false,
          highValueNewThreadsCount: 0,
          highValueNewRepliesCount: 0,
          directRepliesCount: 0,
          newActivityCount: 0,
          permissions: [],
        },
        myPermissions: [],
        threads: {},
        threadIDs: [],
        forumUuidFilter: [],
        currentOrdering: null,
      });
    },
    // syncForumSpace: (spaceGroups: SpaceGroupWithPermissions[]) => {
    //   update((state) => {
    //     for (const sg of spaceGroups) {
    //       for (const sp of sg.spaces) {
    //         if (sp.uuid === state.forumSpace.uuid) {
    //           return { ...state, forumSpace: sp as ForumSpace };
    //         }
    //       }
    //     }

    //     return state;
    //   });
    // },
    // fetch: (uuid: string) => {
    //   return api.forumSpaceRetrieve({ uuid }).then((resp) => {
    //     spaces.updateSpace(resp.data.forumSpace);

    //     update((forum) => ({
    //       ...forum,
    //       fetched: true,
    //       forumSpace: resp.data.forumSpace,
    //       myPermissions: resp.data.myPermissions.permissions || [],
    //     }));
    //   });
    // },
    setForumSpace: (forumSpace: ForumSpace) => {
      update((state) => {
        return {
          ...state,
          forumSpace: {
            ...state.forumSpace,
            ...forumSpace,
          },
        };
      });
    },
    updateThread: (thread: { uuid: string } & Partial<Thread>) => {
      // This sanitization should only be temporary, until we figure out why the backend is returning duplicate repliers
      thread.repliers = distinctByKey(thread.repliers || [], "uuid");

      update((state) => {
        state.threads[thread.uuid] = {
          ...state.threads[thread.uuid],
          ...thread,
        };
        return state;
      });
    },
    removeThread: (uuid: string) => {
      update((state) => {
        return {
          ...state,
          threadIDs: state.threadIDs.filter((thread) => thread !== uuid),
        };
      });
    },
    fetchThreads: (
      communitySlug: string,
      forumSpaceUuid: Array<string>,
      forceRefetchCounter: number,
      filterByForum: boolean,
      chosenSort: ThreadListSort,
      filter: ThreadFilter,
      labels: string[],
      append: boolean,
      cursor?: string
    ): Promise<NextPrevious> => {
      const showSolvedThreads = filter === ThreadFilter.Solved;
      const solvedFilterSelected = [ThreadFilter.Solved, ThreadFilter.Unsolved].includes(filter);
      return new Promise<NextPrevious>((resolve) => {
        if (fetchDebounce) clearTimeout(fetchDebounce);
        // debounce updates
        fetchDebounce = setTimeout(async () => {
          const threads = await fetchThreads(
            communitySlug,
            forumSpaceUuid,
            forceRefetchCounter,
            chosenSort,
            !filterByForum,
            filter === ThreadFilter.Unread,
            labels,
            solvedFilterSelected ? showSolvedThreads : undefined,
            cursor
          );
          const { nextCursor, results } = threads.data;
          // @ts-ignore
          setAllThreads(results, chosenSort, forumSpaceUuid, append);
          resolve({
            nextCursor: nextCursor || undefined,
            previousCursor: cursor || undefined,
          });
        }, 300);
      });
    },
    setAllThreads,
    addThreads: (threadList: Thread[]) => {
      update((state) => {
        for (const thread of threadList) {
          state.threads[thread.uuid] = thread;
          state.threadIDs.push(thread.uuid);
        }
        return state;
      });
    },
    clearAllThreads: () => {
      update((state) => {
        return {
          ...state,
          threads: {},
          threadIDs: [],
          allUnreadThreads: [],
          allSolvedThreads: [],
          allUnsolvedThreads: [],
        };
      });
    },
    setActivitySubscribeStatus: (isSubscribedToActivity: boolean) => {
      update((state) => {
        return {
          ...state,
          forumSpace: {
            ...state.forumSpace,
            isSubscribedToActivity,
          },
        };
      });
    },
    updateDescription: (description: string) => {
      update((state) => {
        api.forumSpacePartialUpdate({
          uuid: state.forumSpace.uuid,
          patchedForumSpace: { description },
        });

        return state;
      });
      update((state) => {
        return {
          ...state,
          forumSpace: {
            ...state.forumSpace,
            description,
          },
        };
      });
    },
    async favourite(forumUuid: string) {
      if (get(this).forumSpace.uuid === forumUuid) {
        update((state) => ({
          ...state,
          forumSpace: {
            ...state.forumSpace,
            favourite: true,
          },
        }));
      }
      await favourites.add({ uuid: forumUuid, type: "forum" });
    },
    async unfavourite(forumUuid: string) {
      if (get(this).forumSpace.uuid === forumUuid) {
        update((state) => ({
          ...state,
          forumSpace: {
            ...state.forumSpace,
            favourite: false,
          },
        }));
      }

      await favourites.remove({ uuid: forumUuid, type: "forum" });
    },
  };
};

export const forum = forumStore();
export const forumSpace = derived(forum, ($forum) => $forum.forumSpace);
export const forumUuid = derived(forumSpace, ($forumSpace) => $forumSpace.uuid);
export const threadIndex = derived(forum, ($forum) => $forum.threads);
export const threadIDs = derived(forum, ($forum) => $forum.threadIDs);
export const threads = derived([threadIndex, threadIDs], ([$threadIndex, $threadIDs]) =>
  $threadIDs.map((id) => $threadIndex[id])
);
export const isThreadUpvotesEnabled = derived(
  forumSpace,
  ($forumSpace) => !!$forumSpace.threadUpvotesEnabled
);
export const isReplyUpvotesEnabled = derived(
  forumSpace,
  ($forumSpace) => !!$forumSpace.postUpvotesEnabled
);
